import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useTutorProfileApproval } from '@/hooks/useTutorProfileApproval';
import { useAuth } from '@/hooks/useAuth';
import { isTutor } from '@/utils/roleValidation';
import { pb } from '@/lib/pocketbase';

interface ProfileUpdateNotification {
  id: string;
  type: 'update_submitted' | 'update_approved' | 'update_rejected';
  message: string;
  isRead: boolean;
  createdAt: string;
  updateRequestId: string;
}

const PendingUpdateNotification = () => {
  const { user } = useAuth();
  const { hasActivePendingUpdate, checkPendingUpdates } = useTutorProfileApproval();
  const [notifications, setNotifications] = useState<ProfileUpdateNotification[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch notifications for current tutor
  const fetchNotifications = async () => {
    if (!user || !isTutor(user)) return;

    try {
      setLoading(true);
      // Get tutor profile first
      const tutorProfiles = await pb.collection('tutors').getList(1, 1, {
        filter: `user = "${user.id}"`
      });

      if (tutorProfiles.items.length === 0) return;

      const tutorId = tutorProfiles.items[0].id;

      // Skip notifications fetch - collection doesn't exist or has permission issues
      // TODO: Create proper notifications system
      setNotifications([]);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      await pb.collection('profile_update_notifications').update(notificationId, {
        isRead: true
      });
      
      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const updatePromises = notifications.map(notification =>
        pb.collection('profile_update_notifications').update(notification.id, {
          isRead: true
        })
      );
      
      await Promise.all(updatePromises);
      setNotifications([]);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  useEffect(() => {
    if (user && isTutor(user)) {
      fetchNotifications();
      checkPendingUpdates();
    }
  }, [user]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'update_submitted':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'update_approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'update_rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-blue-600" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'update_submitted':
        return 'border-yellow-200 bg-yellow-50';
      case 'update_approved':
        return 'border-green-200 bg-green-50';
      case 'update_rejected':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-blue-200 bg-blue-50';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Don't show if not a tutor or no notifications
  if (!user || !isTutor(user) || notifications.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 mb-6">
      {/* Pending Update Alert */}
      {hasActivePendingUpdate === true && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <Clock className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            You have a profile update pending admin approval. Any additional changes will create a new approval request.
          </AlertDescription>
        </Alert>
      )}

      {/* Notification Messages */}
      {notifications.map((notification) => (
        <Alert key={notification.id} className={getNotificationColor(notification.type)}>
          <div className="flex items-start justify-between w-full">
            <div className="flex items-start gap-3 flex-1">
              {getNotificationIcon(notification.type)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium mb-1">
                  {notification.type === 'update_submitted' && 'Update Submitted'}
                  {notification.type === 'update_approved' && 'Update Approved'}
                  {notification.type === 'update_rejected' && 'Update Rejected'}
                </p>
                <p className="text-sm text-gray-700">{notification.message}</p>
                <p className="text-xs text-gray-500 mt-1">{formatDate(notification.createdAt)}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAsRead(notification.id)}
              className="ml-2 h-6 px-2 text-xs"
            >
              Dismiss
            </Button>
          </div>
        </Alert>
      ))}

      {/* Mark All as Read Button */}
      {notifications.length > 1 && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={markAllAsRead}
            className="text-xs"
          >
            Mark All as Read
          </Button>
        </div>
      )}
    </div>
  );
};

export default PendingUpdateNotification;